import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { adminApi, getShippingQuote } from "../../lib/api";
import { eur, STATUS_LABELS } from "../../lib/format";
import { toast } from "sonner";
import { ArrowLeft, Search, Plus, Trash2, Save, User, PenLine } from "lucide-react";

const EMPTY_CUSTOMER = { customer_id: "", customer_name: "", company: "", cif: "", email: "", phone: "", address: "", postal_code: "", city: "", province: "", country: "España" };

export default function AdminOrderForm() {
  const { id } = useParams();
  const editing = Boolean(id);
  const nav = useNavigate();

  const [mode, setMode] = useState("existing"); // existing | manual
  const [cust, setCust] = useState(EMPTY_CUSTOMER);
  const [custSearch, setCustSearch] = useState("");
  const [custResults, setCustResults] = useState([]);
  const [items, setItems] = useState([]);
  const [prodSearch, setProdSearch] = useState("");
  const [prodResults, setProdResults] = useState([]);
  const [coupon, setCoupon] = useState("");
  const [manualDiscount, setManualDiscount] = useState(0);
  const [shippingOverride, setShippingOverride] = useState("");
  const [shipQuote, setShipQuote] = useState(null);
  const [status, setStatus] = useState("pedido_recibido");
  const [observations, setObservations] = useState("");
  const [sendEmail, setSendEmail] = useState(false);
  const [genInvoice, setGenInvoice] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) return;
    adminApi.get(`/admin/orders/${id}`).then((r) => {
      const o = r.data;
      setMode("manual");
      setCust({ customer_id: o.customer_id || "", customer_name: o.customer_name || "", company: o.company || "", cif: o.cif || "", email: o.email || "", phone: o.phone || "", address: o.address || "", postal_code: o.postal_code || "", city: o.city || "", province: o.province || "", country: o.country || "España" });
      setItems((o.items || []).map((it) => ({ product_id: it.product_id || "", name: it.name, reference: it.reference || "", price: Number(it.price), quantity: Number(it.quantity) })));
      setCoupon(o.coupon_code || "");
      setStatus(o.status || "pedido_recibido");
      setObservations(o.observations || "");
      if (o.shipping_status === "manual") setShippingOverride(String(o.shipping ?? ""));
    });
  }, [id, editing]);

  const searchCustomers = async () => {
    const r = await adminApi.get("/admin/customers", { params: { search: custSearch } });
    setCustResults(r.data.slice(0, 8));
  };
  const pickCustomer = (c) => {
    setCust({ customer_id: c.id, customer_name: c.name || "", company: c.company || "", cif: c.cif || "", email: c.email || "", phone: c.phone || "", address: c.address || "", postal_code: c.postal_code || "", city: c.city || "", province: c.province || "", country: c.country || "España" });
    setCustResults([]);
    setCustSearch("");
  };

  const searchProducts = async () => {
    const r = await adminApi.get("/admin/products", { params: { search: prodSearch } });
    setProdResults(r.data.slice(0, 8));
  };
  const addProduct = (p) => {
    setItems((prev) => [...prev, { product_id: p.id, name: p.name, reference: p.reference || "", price: Number(p.price) || 0, quantity: 1 }]);
    setProdResults([]);
    setProdSearch("");
  };
  const addManualLine = () => setItems((prev) => [...prev, { product_id: "", name: "", reference: "", price: 0, quantity: 1 }]);
  const updateItem = (idx, field, value) => setItems((prev) => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const setC = (k, v) => setCust((c) => ({ ...c, [k]: v }));

  const subtotal = items.reduce((s, it) => s + (Number(it.price) || 0) * (Number(it.quantity) || 0), 0);
  const baseAmt = Math.max(subtotal - (Number(manualDiscount) || 0), 0);
  useEffect(() => {
    const clean = items.filter((it) => Number(it.quantity) > 0);
    if (!clean.length) { setShipQuote(null); return; }
    const t = setTimeout(() => {
      getShippingQuote({ items: clean.map((it) => ({ product_id: it.product_id || "", name: it.name || "-", reference: it.reference || "", price: Number(it.price) || 0, quantity: Number(it.quantity) })), province: cust.province || "", coupon_code: coupon })
        .then(setShipQuote).catch(() => setShipQuote(null));
    }, 400);
    return () => clearTimeout(t);
  }, [items, cust.province, coupon]);
  const shippingAmt = shippingOverride !== "" ? (Number(shippingOverride) || 0) : (shipQuote && shipQuote.status !== "consultar" ? shipQuote.cost : 0);
  const iva = (baseAmt + shippingAmt) * 0.21;

  const submit = async () => {
    if (!cust.customer_name.trim()) { toast.error("Indique el nombre del cliente"); return; }
    const clean = items.filter((it) => it.name.trim() && Number(it.quantity) > 0).map((it) => ({ ...it, price: Number(it.price) || 0, quantity: Number(it.quantity) }));
    if (clean.length === 0) { toast.error("Añada al menos un producto"); return; }
    setSaving(true);
    try {
      const payload = { ...cust, coupon_code: coupon, manual_discount: Number(manualDiscount) || 0, status, observations, items: clean, shipping: shippingOverride !== "" ? Number(shippingOverride) || 0 : null };
      if (editing) {
        await adminApi.put(`/admin/orders/${id}`, payload);
        toast.success("Pedido actualizado");
        nav(`/admin/pedidos/${id}`);
      } else {
        const r = await adminApi.post("/admin/orders", { ...payload, send_email: sendEmail, generate_invoice: genInvoice });
        toast.success(`Pedido ${r.data.number} creado`);
        nav(`/admin/pedidos/${r.data.id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar el pedido");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-4xl" data-testid="order-form">
      <button onClick={() => nav("/admin/pedidos")} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-brand mb-4"><ArrowLeft className="h-4 w-4" /> Volver a pedidos</button>
      <h1 className="font-heading font-bold text-3xl tracking-tighter mb-6">{editing ? "Editar pedido" : "Nuevo pedido"}</h1>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {/* Customer */}
          <div className="bg-white border border-border rounded-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="font-heading font-semibold">Datos del cliente</p>
              {!editing && (
                <div className="flex gap-1 border border-border rounded-sm p-1 bg-secondary">
                  <button onClick={() => setMode("existing")} className={`px-3 py-1 text-xs rounded-sm inline-flex items-center gap-1 ${mode === "existing" ? "bg-white text-brand shadow-sm" : "text-muted-foreground"}`} data-testid="mode-existing"><User className="h-3 w-3" /> Cliente existente</button>
                  <button onClick={() => setMode("manual")} className={`px-3 py-1 text-xs rounded-sm inline-flex items-center gap-1 ${mode === "manual" ? "bg-white text-brand shadow-sm" : "text-muted-foreground"}`} data-testid="mode-manual"><PenLine className="h-3 w-3" /> Manual</button>
                </div>
              )}
            </div>

            {mode === "existing" && !editing && (
              <div className="mb-3">
                <div className="relative flex gap-2">
                  <input value={custSearch} onChange={(e) => setCustSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), searchCustomers())} placeholder="Buscar cliente por nombre, empresa o email..." className="flex-1 pl-3 pr-3 py-2 text-sm border border-border rounded-sm" data-testid="customer-search-input" />
                  <button onClick={searchCustomers} className="inline-flex items-center gap-1 border border-border rounded-sm px-3 text-sm hover:border-brand"><Search className="h-4 w-4" /></button>
                </div>
                {custResults.length > 0 && (
                  <div className="border border-border rounded-sm mt-1 divide-y divide-border max-h-56 overflow-y-auto">
                    {custResults.map((c) => (
                      <button key={c.id} onClick={() => pickCustomer(c)} className="w-full text-left px-3 py-2 text-sm hover:bg-brand/5" data-testid={`customer-result-${c.id}`}>
                        <span className="font-medium">{c.name}</span> {c.company && <span className="text-muted-foreground">· {c.company}</span>}
                        <span className="block text-xs text-muted-foreground">{c.email}</span>
                      </button>
                    ))}
                  </div>
                )}
                {cust.customer_id && <p className="text-xs text-emerald-700 mt-2">Cliente seleccionado: <strong>{cust.customer_name}</strong></p>}
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-3">
              <input value={cust.customer_name} onChange={(e) => setC("customer_name", e.target.value)} placeholder="Nombre y apellidos *" className="px-3 py-2 text-sm border border-border rounded-sm" data-testid="cust-name" />
              <input value={cust.company} onChange={(e) => setC("company", e.target.value)} placeholder="Empresa" className="px-3 py-2 text-sm border border-border rounded-sm" data-testid="cust-company" />
              <input value={cust.cif} onChange={(e) => setC("cif", e.target.value)} placeholder="CIF / NIF" className="px-3 py-2 text-sm border border-border rounded-sm" data-testid="cust-cif" />
              <input value={cust.email} onChange={(e) => setC("email", e.target.value)} placeholder="Email" className="px-3 py-2 text-sm border border-border rounded-sm" data-testid="cust-email" />
              <input value={cust.phone} onChange={(e) => setC("phone", e.target.value)} placeholder="Teléfono" className="px-3 py-2 text-sm border border-border rounded-sm" data-testid="cust-phone" />
              <input value={cust.address} onChange={(e) => setC("address", e.target.value)} placeholder="Dirección" className="px-3 py-2 text-sm border border-border rounded-sm" data-testid="cust-address" />
              <input value={cust.postal_code} onChange={(e) => setC("postal_code", e.target.value)} placeholder="Código postal" className="px-3 py-2 text-sm border border-border rounded-sm" data-testid="cust-cp" />
              <input value={cust.city} onChange={(e) => setC("city", e.target.value)} placeholder="Población" className="px-3 py-2 text-sm border border-border rounded-sm" data-testid="cust-city" />
              <input value={cust.province} onChange={(e) => setC("province", e.target.value)} placeholder="Provincia" className="px-3 py-2 text-sm border border-border rounded-sm" data-testid="cust-province" />
            </div>
          </div>

          {/* Items */}
          <div className="bg-white border border-border rounded-sm p-4">
            <p className="font-heading font-semibold mb-3">Productos</p>
            <div className="relative flex gap-2 mb-2">
              <input value={prodSearch} onChange={(e) => setProdSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), searchProducts())} placeholder="Buscar producto por nombre o referencia..." className="flex-1 pl-3 pr-3 py-2 text-sm border border-border rounded-sm" data-testid="product-search-input" />
              <button onClick={searchProducts} className="inline-flex items-center gap-1 border border-border rounded-sm px-3 text-sm hover:border-brand"><Search className="h-4 w-4" /></button>
              <button onClick={addManualLine} className="inline-flex items-center gap-1 border border-border rounded-sm px-3 text-sm hover:border-brand whitespace-nowrap" data-testid="add-manual-line"><Plus className="h-4 w-4" /> Línea manual</button>
            </div>
            {prodResults.length > 0 && (
              <div className="border border-border rounded-sm mb-3 divide-y divide-border max-h-56 overflow-y-auto">
                {prodResults.map((p) => (
                  <button key={p.id} onClick={() => addProduct(p)} className="w-full text-left px-3 py-2 text-sm hover:bg-brand/5 flex justify-between" data-testid={`product-result-${p.id}`}>
                    <span>{p.name} <span className="text-xs font-mono text-muted-foreground">{p.reference}</span></span>
                    <span className="font-medium">{eur(p.price)}</span>
                  </button>
                ))}
              </div>
            )}
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1">Producto</th><th className="w-20 text-center">Cant.</th><th className="w-28 text-right">Precio</th><th className="w-24 text-right">Subtotal</th><th className="w-8"></th></tr></thead>
              <tbody>
                {items.length === 0 ? <tr><td colSpan={5} className="py-6 text-center text-muted-foreground text-sm">Sin líneas. Busque un producto o añada una línea manual.</td></tr> :
                 items.map((it, idx) => (
                  <tr key={idx} className="border-t border-border" data-testid={`order-line-${idx}`}>
                    <td className="py-1.5 pr-2">
                      <input value={it.name} onChange={(e) => updateItem(idx, "name", e.target.value)} placeholder="Nombre del producto" className="w-full px-2 py-1 text-sm border border-border rounded-sm" data-testid={`line-name-${idx}`} />
                      <input value={it.reference} onChange={(e) => updateItem(idx, "reference", e.target.value)} placeholder="Ref." className="w-full px-2 py-0.5 mt-1 text-xs border border-border rounded-sm font-mono" />
                    </td>
                    <td className="text-center"><input type="number" min="1" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} className="w-16 px-2 py-1 text-sm border border-border rounded-sm text-center" data-testid={`line-qty-${idx}`} /></td>
                    <td className="text-right"><input type="number" step="0.01" min="0" value={it.price} onChange={(e) => updateItem(idx, "price", e.target.value)} className="w-24 px-2 py-1 text-sm border border-border rounded-sm text-right" data-testid={`line-price-${idx}`} /></td>
                    <td className="text-right font-medium">{eur((Number(it.price) || 0) * (Number(it.quantity) || 0))}</td>
                    <td className="text-right"><button onClick={() => removeItem(idx)} className="p-1 text-red-500 hover:text-red-700" data-testid={`remove-line-${idx}`}><Trash2 className="h-4 w-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white border border-border rounded-sm p-4">
            <label className="text-sm font-medium block mb-1">Observaciones</label>
            <textarea value={observations} onChange={(e) => setObservations(e.target.value)} rows={2} className="w-full px-3 py-2 text-sm border border-border rounded-sm" data-testid="order-observations" />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-5">
          <div className="bg-white border border-border rounded-sm p-4 space-y-3">
            <div>
              <label className="text-sm font-medium block mb-1">Estado</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full px-3 py-2 text-sm border border-border rounded-sm bg-white" data-testid="order-status-select">
                {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Cupón (opcional)</label>
              <input value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="Código de cupón" className="w-full px-3 py-2 text-sm border border-border rounded-sm" data-testid="order-coupon" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Descuento manual (€)</label>
              <input type="number" step="0.01" min="0" value={manualDiscount} onChange={(e) => setManualDiscount(e.target.value)} className="w-full px-3 py-2 text-sm border border-border rounded-sm" data-testid="order-manual-discount" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Portes (€) <span className="text-xs text-muted-foreground font-normal">vacío = tarifa automática</span></label>
              <input type="number" step="0.01" min="0" value={shippingOverride} onChange={(e) => setShippingOverride(e.target.value)} placeholder={shipQuote ? (shipQuote.status === "consultar" ? "a consultar" : String(shipQuote.cost)) : ""} className="w-full px-3 py-2 text-sm border border-border rounded-sm" data-testid="order-shipping-override" />
              {shipQuote && <p className="text-[11px] text-muted-foreground mt-1">{shipQuote.label}</p>}
            </div>
          </div>

          <div className="bg-white border border-border rounded-sm p-4 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono">{eur(subtotal)}</span></div>
            {Number(manualDiscount) > 0 && <div className="flex justify-between text-emerald-700"><span>Descuento</span><span className="font-mono">−{eur(Number(manualDiscount))}</span></div>}
            <div className="flex justify-between"><span className="text-muted-foreground">Portes</span><span className="font-mono">{shippingOverride === "" && shipQuote && shipQuote.status === "consultar" ? "a consultar" : eur(shippingAmt)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">IVA (21%)</span><span className="font-mono">{eur(iva)}</span></div>
            <div className="flex justify-between font-bold pt-1"><span>Total aprox.</span><span className="text-brand">{eur(baseAmt + shippingAmt + iva)}</span></div>
            {coupon && <p className="text-xs text-muted-foreground pt-1">El cupón se validará al guardar.</p>}
          </div>

          {!editing && (
            <div className="bg-white border border-border rounded-sm p-3 space-y-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} data-testid="send-email-check" />
                Enviar email de confirmación al cliente
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={genInvoice} onChange={(e) => setGenInvoice(e.target.checked)} data-testid="gen-invoice-check" />
                Generar factura al crear el pedido
              </label>
            </div>
          )}

          <button onClick={submit} disabled={saving} className="w-full inline-flex items-center justify-center gap-2 bg-brand text-white py-3 rounded-sm font-semibold hover:bg-brand-dark disabled:opacity-60" data-testid="save-order-btn">
            <Save className="h-4 w-4" /> {saving ? "Guardando..." : editing ? "Guardar cambios" : "Crear pedido"}
          </button>
        </div>
      </div>
    </div>
  );
}
